import React, { useState, useEffect } from 'react';
import { Plus, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_CFG = {
  low:    { label: 'Low',    color: 'text-slate-500  bg-slate-50 border-slate-200'  },
  medium: { label: 'Medium', color: 'text-amber-700  bg-amber-50 border-amber-200'  },
  high:   { label: 'High',   color: 'text-red-700    bg-red-50   border-red-200'    },
};

export default function BreakdownDialog({ open, onOpenChange, task, onApply }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({});

  useEffect(() => {
    if (!open || !task?.task_id) return;
    setLoading(true);
    setSuggestions([]);
    setSelected(new Set());
    setEdits({});
    fetch(`${API}/tasks/${task.task_id}/break-down`, {
      method: 'POST', credentials: 'include',
    })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || 'Break-down failed');
        }
        return r.json();
      })
      .then(({ subtasks }) => {
        setSuggestions(subtasks || []);
        setSelected(new Set((subtasks || []).map((_, i) => i)));
      })
      .catch(e => toast.error(e.message || 'AI break-down failed'))
      .finally(() => setLoading(false));
  }, [open, task?.task_id]);

  const toggle = (i) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === suggestions.length ? new Set() : new Set(suggestions.map((_, i) => i)));
  };
  const editTitle = (i, title) => setEdits(prev => ({ ...prev, [i]: title }));

  const submit = () => {
    if (selected.size === 0) {
      toast.error('Pick at least one subtask');
      return;
    }
    const picked = Array.from(selected).sort((a, b) => a - b).map(i => ({
      title: edits[i] ?? suggestions[i].title,
      priority: suggestions[i].priority,
    }));
    onApply(picked);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }} className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-[#2D6B68]" /> AI break down
          </DialogTitle>
        </DialogHeader>
        {task && (
          <p className="text-sm text-[#6B7A73]">
            Turning <span className="font-medium text-[#1A1D1C]">{task.title}</span> into concrete subtasks.
          </p>
        )}

        {loading ? (
          <div className="py-12 text-center space-y-2">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-xs text-[#6B7A73]">Gemini is thinking…</p>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#6B7A73]">No suggestions returned. Try editing the task title for more context.</div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-[#6B7A73] pb-1 border-b border-[#E6EBE8]">
              <button onClick={toggleAll} className="font-semibold uppercase tracking-wider hover:text-[#2D6B68]" data-testid="toggle-all-breakdown">
                {selected.size === suggestions.length ? 'Deselect all' : 'Select all'}
              </button>
              <span>{selected.size} / {suggestions.length} selected</span>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1 pr-1">
              {suggestions.map((s, i) => {
                const checked = selected.has(i);
                return (
                  <div key={i} className={`flex items-start gap-2 p-2 rounded ${checked ? 'bg-[#F5FAF9]' : ''}`} data-testid={`suggestion-${i}`}>
                    <Checkbox checked={checked} onCheckedChange={() => toggle(i)} className="mt-1.5" />
                    <Input value={edits[i] ?? s.title} onChange={e => editTitle(i, e.target.value)} className="flex-1 bg-white border-[#E6EBE8] text-sm" />
                    <span className={`text-[10px] px-1.5 py-1.5 rounded border self-center whitespace-nowrap ${PRIORITY_CFG[s.priority]?.color || ''}`}>
                      {PRIORITY_CFG[s.priority]?.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">
            Cancel
          </Button>
          <Button onClick={submit} disabled={loading || selected.size === 0} className="bg-[#2D6B68] text-white hover:bg-[#235552]" data-testid="apply-breakdown-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add {selected.size} subtask{selected.size !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
